'use client';

import { readStoredConsent, writeConsent } from '@/lib/cookie-consent';
import { isLikelyNativeAppShell } from '@/lib/native-shell-detect';
import { pwaPrimarySolidClass } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';

/** Sentry SDK — отдельный чанк, только после согласия (см. текст баннера). */
const SentryInitLazy = dynamic(
  () => import('@/components/SentryInit').then((mod) => ({ default: mod.SentryInit })),
  { ssr: false }
);

export function CookieConsentRoot({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored !== null) {
      setConsent(true);
      setReady(true);
      return;
    }
    // APK/WebView: не блокируем вход баннером — согласие фиксируем сразу (политика в /privacy).
    if (isLikelyNativeAppShell()) {
      writeConsent();
      setConsent(true);
      setReady(true);
      return;
    }
    setConsent(false);
    setReady(true);
  }, []);

  const accept = useCallback(() => {
    writeConsent();
    setConsent(true);
  }, []);

  return (
    <>
      {consent ? <SentryInitLazy /> : null}
      {children}
      {ready && !consent ? (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] mb-4 sm:px-4 sm:mb-5"
          role="region"
          aria-label="Согласие на использование cookie и обработку данных"
        >
          <div
            className={cn(
              'pointer-events-auto mx-auto flex max-w-md flex-row items-center justify-between gap-2 rounded-2xl border border-[var(--border)]',
              'bg-[var(--surface)]/95 px-3 py-2 shadow-[0_8px_32px_rgba(0,0,0,0.12)] backdrop-blur-md',
              'dark:bg-[var(--background)]/95 dark:shadow-[0_8px_32px_rgba(0,0,0,0.45)]',
            )}
          >
            <p className="text-xs leading-snug text-[var(--muted-foreground)] sm:text-sm">
              Cookie нужны для входа и работы приложения. Подробнее — в{' '}
              <Link href="/privacy" className="font-medium text-primary-600 underline underline-offset-2 hover:text-primary-700 dark:text-primary-400">
                политике конфиденциальности
              </Link>
              .
            </p>
            <button
              type="button"
              onClick={accept}
              className={cn(
                pwaPrimarySolidClass,
                'shrink-0 rounded-full px-4 py-2 text-xs font-semibold shadow-sm sm:text-sm',
              )}
            >
              Согласен
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
